import React from 'react';
import { Box, Typography, Stack, Chip, Divider, IconButton, Paper } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Subject as SubjectIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import MobileHeader from '../components/MobileHeader';
import colors from '../common/Colors';
import { formatInBusinessTimezone } from '../utils/dateTimeHelper';

const LessonPlanDetailsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const item = location.state?.item;

  if (!item) {
    navigate('/lesson-plans');
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatInBusinessTimezone(dateString, 'dd MMM yyyy');
  };

  const handleDownload = () => {
    if (item.fileUrl) {
      window.open(item.fileUrl, '_blank');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f5f5' }}>
      <MobileHeader title="Lesson Plan Details" showBack={true} />

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, pr: 1 }}>
                {item.topic}
              </Typography>
              <Chip
                label={item.status}
                size="small"
                sx={{
                  bgcolor: colors.SUCCESS,
                  color: '#fff',
                }}
              />
            </Stack>

            <Divider />

            {/* Class and Section */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                  <SchoolIcon sx={{ fontSize: 18, color: colors.GRAY }} />
                  <Typography variant="caption" color="text.secondary">
                    Class
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight="500">
                  {item.className}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Section
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {item.sectionName}
                </Typography>
              </Box>
            </Stack>

            {/* Subject */}
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                <SubjectIcon sx={{ fontSize: 18, color: colors.GRAY }} />
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight="500">
                {item.subjectName}
              </Typography>
            </Box>

            <Divider />

            {/* Date Range */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                  <CalendarIcon sx={{ fontSize: 18, color: colors.GRAY }} />
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight="500">
                  {formatDate(item.startDate)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  End Date
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {formatDate(item.endDate)}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            {/* Description */}
            {item.description && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.description}
                </Typography>
              </Box>
            )}

            {/* Learning Outcomes */}
            {item.learningOutcomes && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Learning Outcomes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.learningOutcomes}
                </Typography>
              </Box>
            )}

            {/* Resources */}
            {item.resources && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Resources
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.resources}
                </Typography>
              </Box>
            )}

            <Divider />

            {/* Created By */}
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                <PersonIcon sx={{ fontSize: 18, color: colors.GRAY }} />
                <Typography variant="caption" color="text.secondary">
                  Created By
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight="500">
                {item.createdBy}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                on {formatDate(item.creationTime)}
              </Typography>
            </Box>

            {/* Attachment */}
            {item.fileName && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Attachment
                  </Typography>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: '#f9f9f9',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f0f0f0',
                      },
                    }}
                    onClick={handleDownload}
                  >
                    <AttachFileIcon sx={{ fontSize: 20, color: colors.PRIMARY }} />
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {item.fileName}
                    </Typography>
                    <IconButton size="small" sx={{ color: colors.PRIMARY }}>
                      <DownloadIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Paper>
                </Box>
              </>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default LessonPlanDetailsScreen;
